import { useState } from 'react'
import { DollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMe } from '../api/auth'
import {
  useEntitlements,
  useUpdateCaps,
  useUsageEvents,
  useUsageSummary,
  type EntitlementValue,
} from '../api/usage'
import { formatCurrencyUSD } from '../i18n/formatters'

export function UsagePage() {
  const { t } = useTranslation('billing')
  const { data: me } = useMe()
  const { data: summary, isLoading: loadingSummary } = useUsageSummary()
  const { data: eventsPage } = useUsageEvents({ limit: 50 })
  const { data: entitlements } = useEntitlements()
  const updateCaps = useUpdateCaps()

  const activeRole = me?.memberships.find((m) => m.org_id === me.active_org_id)?.role
  const isOwner = activeRole === 'owner'
  const entitlementsEnabled = !!entitlements?.enabled

  const [editingCaps, setEditingCaps] = useState(false)
  const [dailyCap, setDailyCap] = useState<string>('')
  const [monthlyCap, setMonthlyCap] = useState<string>('')

  function startEdit() {
    if (!summary) return
    setDailyCap(String(summary.period.daily_cap_usd))
    setMonthlyCap(String(summary.period.monthly_cap_usd))
    setEditingCaps(true)
  }

  async function saveCaps(e: React.FormEvent) {
    e.preventDefault()
    await updateCaps.mutateAsync({
      spend_cap_daily_usd: Number(dailyCap),
      spend_cap_monthly_usd: Number(monthlyCap),
    })
    setEditingCaps(false)
  }

  if (loadingSummary || !summary) {
    return <div className="p-8 text-text-secondary">{t('common:states.loading')}</div>
  }

  const dailyPct = capPct(summary.period.daily_used_usd, summary.period.daily_cap_usd)
  const monthlyPct = capPct(summary.period.monthly_used_usd, summary.period.monthly_cap_usd)
  const maxBar = Math.max(
    0.01,
    ...summary.daily_series.map((b) => b.total_usd),
  )

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('usage.title')}</h1>
        <p className="text-sm text-text-secondary">
          {summary.org.name} — {t('usage.subtitleSuffix')}
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <CapCard
          label={t('usage.today')}
          used={summary.period.daily_used_usd}
          cap={summary.period.daily_cap_usd}
          pct={dailyPct}
        />
        <CapCard
          label={t('usage.thisMonth')}
          used={summary.period.monthly_used_usd}
          cap={summary.period.monthly_cap_usd}
          pct={monthlyPct}
        />
      </section>

      {entitlementsEnabled && entitlements && (
        <EntitlementsCard features={entitlements.snapshot.features} />
      )}

      {isOwner && !entitlementsEnabled && (
        <section className="rounded-lg border border-border bg-surface p-4">
          {!editingCaps ? (
            <button
              onClick={startEdit}
              className="text-sm text-primary hover:underline"
            >
              {t('usage.editCaps')}
            </button>
          ) : (
            <form onSubmit={saveCaps} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="block text-xs text-text-secondary">{t('usage.dailyCap')}</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                    value={dailyCap}
                    onChange={(e) => setDailyCap(e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-xs text-text-secondary">{t('usage.monthlyCap')}</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                    value={monthlyCap}
                    onChange={(e) => setMonthlyCap(e.target.value)}
                  />
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={updateCaps.isPending}
                  className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                  {t('common:actions.save')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingCaps(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm"
                >
                  {t('common:actions.cancel')}
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      <section className="rounded-lg border border-border bg-surface p-4">
        <header className="mb-3 flex items-center gap-2 font-medium">
          <DollarSign className="h-4 w-4 text-text-secondary" />
          {t('usage.last30Days')}
        </header>
        <div className="flex h-28 items-end gap-1">
          {summary.daily_series.map((b) => {
            const pct = (b.total_usd / maxBar) * 100
            return (
              <div
                key={b.day_start}
                title={`${new Date(b.day_start * 1000).toLocaleDateString()} — ${formatCurrencyUSD(b.total_usd, 2)} (${b.event_count} events)`}
                className="flex-1 rounded-t bg-primary/70"
                style={{ height: `${Math.max(pct, 1)}%` }}
              />
            )
          })}
        </div>
        <div className="mt-2 flex justify-between text-xs text-text-secondary">
          <span>
            {new Date(summary.daily_series[0].day_start * 1000).toLocaleDateString()}
          </span>
          <span>{t('usage.todayLabel')}</span>
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="border-b border-border px-4 py-3 font-medium">
          {t('usage.recentEvents')}
        </header>
        {!eventsPage || eventsPage.events.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-secondary">
            {t('usage.emptyEvents')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-2 text-left">{t('usage.table.when')}</th>
                  <th className="px-4 py-2 text-left">{t('usage.table.operation')}</th>
                  <th className="px-4 py-2 text-left">{t('usage.table.event')}</th>
                  <th className="px-4 py-2 text-right">{t('usage.table.charge')}</th>
                  <th className="px-4 py-2 text-left">{t('usage.table.source')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {eventsPage.events.map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-2 text-text-secondary">
                      {new Date(e.created_at * 1000).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{e.operation_id || '—'}</td>
                    <td className="px-4 py-2 text-xs">{e.event_type}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatCurrencyUSD(e.price_usd, 4)}
                    </td>
                    <td className="px-4 py-2 text-xs text-text-secondary">{e.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function CapCard({
  label, used, cap, pct,
}: { label: string; used: number; cap: number; pct: number }) {
  const { t } = useTranslation('billing')
  const color =
    pct >= 100 ? 'bg-error' : pct >= 80 ? 'bg-warning' : 'bg-success'
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="mt-1 text-xl font-semibold">
        {formatCurrencyUSD(used, 2)} <span className="text-sm font-normal text-text-secondary">/ {formatCurrencyUSD(cap, 2)}</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-elevated">
        <div
          className={`h-full transition-all ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-text-secondary">
        {t('usage.ofCap', { pct: pct.toFixed(0) })}
      </div>
    </div>
  )
}

function capPct(used: number, cap: number): number {
  if (cap <= 0) return 100
  return (used / cap) * 100
}

/**
 * Read-only display for the active org's Stripe Entitlements snapshot.
 *
 * Shown only when the backend reports `enabled: true` (i.e. the
 * `STRIPE_ENTITLEMENTS_ENABLED` flag is on AND the org has been provisioned
 * with active entitlements). Quotas live in Stripe — the dashboard does not
 * mutate them. When `features` is empty, render a hint that the org has no
 * provisioned entitlements yet.
 */
function EntitlementsCard({ features }: { features: Record<string, EntitlementValue> }) {
  const keys = Object.keys(features).sort()
  if (keys.length === 0) {
    return (
      <section className="rounded-lg border border-border bg-surface p-4">
        <header className="mb-2 text-sm font-medium">Entitlements</header>
        <p className="text-sm text-text-secondary">
          No active entitlements. Quotas are configured via Stripe.
        </p>
      </section>
    )
  }
  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <header className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium">Entitlements</span>
        <span className="text-xs text-text-secondary">read-only — managed in Stripe</span>
      </header>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-text-secondary">
          <tr>
            <th className="px-2 py-1 text-left">Feature</th>
            <th className="px-2 py-1 text-right">Limit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {keys.map((key) => {
            const v = features[key]
            const display =
              v.boolean !== null
                ? v.boolean
                  ? 'enabled'
                  : 'disabled'
                : v.numeric !== null
                  ? String(v.numeric)
                  : '—'
            return (
              <tr key={key}>
                <td className="px-2 py-1 font-mono text-xs">{key}</td>
                <td className="px-2 py-1 text-right font-mono">{display}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}
